"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Button from "../ui/Button";
import FadeIn from "../ui/FadeIn";

export default function Footer() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 20) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const scrollToTop = (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <footer className="bg-linear-to-b from-primary to-secondary-dark px-4 py-12 text-white">
      <FadeIn delay={0.1} direction="up" fullWidth>
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col items-start justify-between gap-6 border-b border-white/20 pb-4 md:flex-row md:items-center">
            <div className="flex items-center gap-2">
              <Image
                src="/images/logo/icon.png"
                alt="AdviSify Icon"
                width={32}
                height={32}
                className="h-8 w-8 object-contain"
              />
              <Image
                src="/images/logo/logo.png"
                alt="AdviSify Icon"
                width={100}
                height={32}
                className="h-8 w-28 object-contain"
              />
            </div>
            <div className=" flex flex-wrap gap-4">
              <Button
                variant="outline"
                shape="default"
                size="sm"
                className="flex items-center gap-2.5 rounded-lg border border-[#333] bg-black px-4 py-0 hover:bg-zinc-900"
              >
                <Image
                  src="/images/logo/apple-logo.png"
                  alt="Apple logo"
                  width={20}
                  height={24}
                  className="h-[24px] w-auto object-contain brightness-0 invert"
                />
                <div className="flex flex-col items-start text-white">
                  <span className="text-[10px] font-medium leading-none tracking-wide">
                    Download on the
                  </span>
                  <span className="mt-[2px] text-[14px] font-semibold leading-none tracking-tight">
                    App Store
                  </span>
                </div>
              </Button>
              <Button
                variant="outline"
                shape="default"
                size="sm"
                className="flex items-center gap-2.5 rounded-lg border border-[#333] bg-black px-4 py-2 hover:bg-zinc-900"
              >
                <Image
                  src="/images/logo/googlePlayLogo.png"
                  alt="Google Play logo"
                  width={20}
                  height={24}
                  className="h-[24px] w-auto object-contain"
                />
                <div className="flex flex-col items-start text-white">
                  <span className="text-[10px] font-medium leading-none tracking-wide uppercase">
                    Get It On
                  </span>
                  <span className="mt-[2px] text-[14px] font-semibold leading-none tracking-tight">
                    Google Play
                  </span>
                </div>
              </Button>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 text-[13px] md:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col justify-between">
              <div>
                <p className="font-semibold text-white">Address</p>
                <p className="mt-4 md:mt-6 text-white/70 leading-relaxed">
                  2972 Westheimer Rd. Santa Ana,
                  <br />
                  Illinois 85486
                </p>
              </div>
              <p className="mt-12 md:mt-32 text-xs text-white/50">
                © 2026 — Copyright
              </p>
            </div>
            <div className="flex flex-col justify-between">
              <div>
                <p className="font-semibold text-white">Info</p>
                <ul className="mt-6 space-y-3 text-white/70">
                  <li>
                    <a href="#" className="hover:text-white transition-colors">
                      For patients
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-white transition-colors">
                      Services
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-white transition-colors">
                      Experts
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-white transition-colors">
                      Illnesses
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-white transition-colors">
                      About clinic
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-white transition-colors">
                      Articles
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-white transition-colors">
                      Feedback
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-white transition-colors">
                      Vacancies
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-white transition-colors">
                      Contacts
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex flex-col justify-start">
              <div>
                <p className="font-semibold text-white">Contact us</p>
                <p className="mt-6 text-white/70 hover:text-white cursor-pointer transition-colors">
                  +1 891 989-11-91
                </p>
                <p className="mt-2 text-white/70 hover:text-white cursor-pointer transition-colors">
                  help@logoipsum.com
                </p>
              </div>
            </div>
            <div className="flex flex-col justify-between items-start md:items-end">
              <div className="flex gap-4 mt-6 md:mt-0">
                <a
                  href="#"
                  aria-label="Instagram"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/90 hover:bg-white/20 transition-colors"
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="2"
                      y="2"
                      width="20"
                      height="20"
                      rx="5"
                      ry="5"
                    ></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </a>
                <a
                  href="#"
                  aria-label="Facebook"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/90 hover:bg-white/20 transition-colors"
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                  </svg>
                </a>
                <a
                  href="#"
                  aria-label="YouTube"
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/90 hover:bg-white/20 transition-colors"
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33 2.78 2.78 0 0 0 1.94 2c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.33 29 29 0 0 0-.46-5.33z"></path>
                    <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </FadeIn>
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white text-secondary-darker shadow-2xl transition-all duration-300 hover:bg-slate-100 hover:scale-110 md:bottom-10 md:right-10 ${
          isVisible
            ? "translate-y-0 opacity-100"
            : "translate-y-10 pointer-events-none opacity-0"
        }`}
        aria-label="Scroll to top"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 19V5M5 12l7-7 7 7" />
        </svg>
      </button>
    </footer>
  );
}
